import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import { makeStyles } from "@material-ui/core/styles";
import { Container, Grid, MenuItem, Paper, TextField, useMediaQuery } from "@material-ui/core";
import { VisibilityOutlined } from "@material-ui/icons";
import Lottie from "react-lottie";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import formatDefaultValueForDatetimeInput from "../../utils/datetime/formatDefaultValueForDatetimeInputs";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import NoDataImg from "../../assets/miscSvg/noData.svg";
import QueueOverrideDashboardModal from "../QueueOverrideDashboardModal";
import SearchLottie from "../../assets/lottie/search.json";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";

import DateUtils from "../../utils/classes/DateUtils";
import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import LottieUtils from "../../utils/classes/LottieUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
  mainContainerScroll: {
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    paddingBottom: "20px",
  },

  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  flexContainer: {
		display: "flex",
		justifyContent: "right",
		alignItems: "baseline",
		gap: "15px",
		paddingRight: "10px",
	},

  dashboardContainer: {
		width: "100%",
		height: "400px",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "5px",
		padding: "20px 0px 20px 0px",
		textIndent: "15px",
	},

  emptyBoxContainer: {
    display: "flex",
		width: "100%",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "50px",
  },

  emptyBoxImage: {
    width: "250px",
    height: "250px",
  },

  emptyBoxSpan: {
    fontSize: "20px",
  },

  sectionContainer: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		flexWrap: "wrap",
		[theme.breakpoints.down("sm")]: { justifyContent: "center", gap: "10px", },
	},

  table: {
		// scroll
		"& .MuiDataGrid-virtualScroller": { ...theme.scrollbarStyles, },

		// header
		"& .MuiDataGrid-columnHeaders": {
			backgroundColor: theme.palette.background.paper,
			color: theme.palette.text.primary,
			fontSize: 16,
		},

		// rows
		"& .MuiDataGrid-cell": { color: theme.palette.text.primary, },

		// pair rows
		"& .MuiDataGrid-virtualScrollerRenderZone": {
			"& .MuiDataGrid-row": {
				"&:nth-child(2n)": { backgroundColor: theme.palette.background.paper, }
			}
		},

		// buttons
		"& .MuiButtonBase-root": { color: theme.palette.text.primary, },

		// checkboxes - not checked
		"& [data-testId='CheckBoxOutlineBlankIcon']": { color: theme.palette.text.primary, },

		// checkboxes - checked
		"& [data-testId='CheckBoxIcon']": { color: theme.palette.primary.main, },

		// checkbox - header (unselect all)
		"& [data-testId='IndeterminateCheckBoxIcon']": { color: theme.palette.primary.main, },

		// footer - selected rows
		"& .MuiDataGrid-footerContainer": { color: theme.palette.text.primary, },

		// footer - pagination
		"& .MuiTablePagination-root": { color: theme.palette.text.primary, },
	},

  viewContainer: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		width: "100px",
		maxWidth: "100px",
		minWidth: "100px",
		alignItems: "center",
		gap: "2px",
	},

  viewButton: {
    cursor: "pointer",
    borderRadius: "50%",
  },

  actionButton: {
    "&:hover": { color: theme.palette.primary.main, },
  },
}));

const QueueOverridesDashboard = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [initialDate, setInitialDate] = useState(null);
  const [finalDate, setFinalDate] = useState(null);
  const [queueOverrides, setQueueOverrides] = useState([]);

  const [selectedQueueOverride, setSelectedQueueOverride] = useState(null);
  const [queueOverrideModalOpen, setQueueOverrideModalOpen] = useState(false);

  const { isLoading, queueOverrideAuthorizers, setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  ***************
  //  ** Callbacks **
  //  ***************
  const renderIdColumn = useCallback(row => {
    return (
      <div className={classes.viewContainer}>
        <MenuItem
          className={`${classes.actionButton} ${classes.viewButton}`}
          onClick={() => handleOpenQueueOverrideModal(row)}
        >
          <VisibilityOutlined />
        </MenuItem>
        {row.id}
      </div>
    );
  }, [classes]);

  const renderCreatedAtColumn = useCallback(value => {
    return DateUtils.toLocaleDateString(value);
  }, []);



  //  ***********
  //  ** Memos **
  //  ***********
  const columns = [
    {
      field: "id"
      , type: "number"
      , headerName: i18n.t("queueOverridesDashboard.table.header.id")
      , width: 100
      , align: "center"
      , renderCell: (params) => renderIdColumn(params.row)
    }
    , {
      field: "createdAt"
      , type: "date"
      , headerName: i18n.t("queueOverridesDashboard.table.header.createdAt")
      , width: 130
      , renderCell: (params) => renderCreatedAtColumn(params.value)
    }
    , {
      field: "status"
      , type: "string"
      , headerName: i18n.t("queueOverridesDashboard.table.header.status")
      , width: 130
      , renderCell: (params) => i18n.t(`queueOverridesDashboard.table.statusTypes.${params.row.status}`)
    }
    , {
      field: "requestedByUserName"
      , type: "string"
      , headerName: i18n.t("queueOverridesDashboard.table.header.requestedByUserName")
      , width: 130
    }
    , {
      field: "resolvedByUserName"
      , type: "string"
      , headerName: i18n.t("queueOverridesDashboard.table.header.resolvedByUserName")
      , width: 130
    }
    , {
      field: "requestReason"
      , type: "string"
      , headerName: i18n.t("queueOverridesDashboard.table.header.requestReason")
      , flex: 1
      , minWidth: 250
    }
  ];
  



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (isLoading) return;

    const userIndex = queueOverrideAuthorizers.findIndex(authorizer => authorizer.id === user.id);

    if (userIndex < 0) {
      DefaultFunctionsUtils.redirectToTicketsPage(history);
      setShouldSyncRouteOnMenu(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, queueOverrideAuthorizers, setShouldSyncRouteOnMenu, user, history]);

  useEffect(() => {
    const date = formatDefaultValueForDatetimeInput().split("T")[0];
    setInitialDate(date);
    setFinalDate(date);
  }, []);

  useEffect(() => {
    if (loading || !initialDate || !finalDate) return;

    const delayDebounceFn = setTimeout(() => {
      const fetchQueueOverrides = async () => {
        setLoading(true);

        try {
          const { data } = await api.get("/queueOverridesDashboard", { params: { initialDate, finalDate } });
          setQueueOverrides(data);
        }
        catch (exception) {
          console.log("queueOverridesDashboard useEffect exception:", exception);
          toastError(exception);
        }
        finally {
          setLoading(false);
        }
      };

      fetchQueueOverrides();
    }, 500);

    return () => clearTimeout(delayDebounceFn);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate, finalDate]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleChangeDate = (event, isInitialDate) => {
    const dashboardInitialDate = document.getElementById("dashboardInitialDate");
    const dashboardFinalDate = document.getElementById("dashboardFinalDate");

    if (dashboardInitialDate.value === "" || dashboardFinalDate.value === "") {
      dashboardInitialDate.value = initialDate;
      dashboardFinalDate.value = finalDate;
      return;
    }

    if (dashboardInitialDate.value > dashboardFinalDate.value) {
      toast.info(i18n.t("backendErrors.DASHBOARD_INITIAL_DATE_AFTER_FINAL_DATE"));
      return;
    }

    if (isInitialDate) setInitialDate(event.target.value);
    else setFinalDate(event.target.value);
  };

  const handleOpenQueueOverrideModal = (queueOverride) => {
    setSelectedQueueOverride(queueOverride);
    setQueueOverrideModalOpen(true);
  };

  const handleCloseQueueOverrideModal = () => {
    setSelectedQueueOverride(null);
    setQueueOverrideModalOpen(false);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <MainContainer>
      <QueueOverrideDashboardModal
        open={queueOverrideModalOpen}
        onClose={handleCloseQueueOverrideModal}
        queueOverride={selectedQueueOverride}
      />

      <MainHeader>
        <Title>{i18n.t("queueOverridesDashboard.title")}</Title>
      </MainHeader>

      <div className={classes.mainContainerScroll}>
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            {/* 
              *****************
              ** Date Inputs **
              *****************
            */}
            <Grid item xs={12}>
              <div className={classes.flexContainer}>
                <TextField
                  variant="outlined"
                  margin="normal"
                  type="date"
                  id="dashboardInitialDate"
                  name="dashboardInitialDate"
                  label={i18n.t("dashboard.fields.initialDate")}
                  value={initialDate}
                  onChange={(event) => handleChangeDate(event, true)}
                />

                <TextField
                  variant="outlined"
                  margin="normal"
                  type="date"
                  id="dashboardFinalDate"
                  name="dashboardFinalDate"
                  label={i18n.t("dashboard.fields.finalDate")}
                  value={finalDate}
                  onChange={(event) => handleChangeDate(event, false)}
                />
              </div>
            </Grid>

            {/* 
              ***********
              ** Table **
              ***********
            */}
            {queueOverrides.length > 0 && (
              <>
                {/* ***---- Search Animation ----*** */}
                {isLoading && (
                  <Grid item xs={12} className={classes.sectionContainer}>
                    <Lottie
                      options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: SearchLottie}}
                      isClickToPauseDisabled={true}
                      width={isSmallScreen ? "100%" : "50%"}
                    />
                  </Grid>
                )}

                {/* ***---- Table ----*** */}
                {!isLoading && (
                  <Grid item xs={12}>
                    <div style={{ height: 500, width: "100%" }}>
                      <DataGrid
                        className={classes.table}
                        rows={queueOverrides}
                        columns={columns}
                        rowsPerPageOptions={[7, 25, 50, 100]}
                        checkboxSelection={true}
                        disableColumnMenu={false}
                      />
                    </div>
                  </Grid>
                )}
              </>
            )}

            {/* 
              *************
              ** No Data **
              *************
            */}
            {queueOverrides.length === 0 && (
              <Paper className={classes.dashboardContainer}>
                <div className={classes.emptyBoxContainer}>
                  <img className={classes.emptyBoxImage} src={NoDataImg} alt="No Data" />
                  <span className={classes.emptyBoxSpan}>{i18n.t("dashboard.emptyBox")}</span>
                </div>
              </Paper>
            )}
          </Grid>
        </Container>
      </div>
    </MainContainer>
  );
};

export default QueueOverridesDashboard;
